import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { ThemePalette } from '@angular/material/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { BuddyInstanceInterface } from 'src/app/models/interface/buddy/buddy-instance.interface';
import { UserInterface } from 'src/app/models/interface/user.interface';
import { HelperService } from 'src/app/services/helper/helper.service';
import { BuddyStore } from 'src/app/store/buddy/component-store/buddy.store';

export type DialogBuddyActivityReportData = {
  studyId?: number;
  members?: UserInterface[];
  buddyInstance?: BuddyInstanceInterface;
};

@Component({
  selector: 'app-dialog-buddy-activity-report',
  templateUrl: './dialog-buddy-activity-report.component.html',
  styleUrls: ['./dialog-buddy-activity-report.component.scss'],
  providers: [BuddyStore],
})
export class DialogBuddyActivityReportComponent implements OnDestroy, OnInit {
  // Buddy activity form submission
  public buddyActivityForm: UntypedFormGroup;

  public getCSVBuddyActivitiesByBuddyIdResponse = new BehaviorSubject<'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE'>('DEFAULT');
  public getCSVBuddyActivitiesByStudyIdResponse = new BehaviorSubject<'DEFAULT' | 'LOADING' | 'SUCCESS' | 'FAILURE'>('DEFAULT');

  public members$: Observable<UserInterface[]>;

  // Ngx datetimepicker
  public showSpinners = false;
  public showSeconds = false;
  public touchUi = false;
  public enableMeridian = false;
  public stepHour = 1;
  public stepMinute = 1;
  public stepSecond = 1;
  public color: ThemePalette = 'primary';

  public studyId: number;
  public members: UserInterface[] = [];
  public buddyInstance: BuddyInstanceInterface;

  public filter = { userSelection: '' };

  private getCSVBuddyActivitiesByBuddyIdResponse$: Observable<any>;

  private getCSVBuddyActivitiesByStudyIdResponse$: Observable<any>;

  private membersSubject = new BehaviorSubject<UserInterface[]>([]);

  private subscriptions: Subscription[] = [];

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DialogBuddyActivityReportData,
    private helperService: HelperService,
    private buddyStore: BuddyStore,
  ) {
    // studyId is set for generating the report of a study.
    // buddyInstance is set for generating the report of a buddy-pair.
    if (this.data.studyId) {
      this.studyId = this.data.studyId;
    } else if (this.data.buddyInstance) {
      this.buddyInstance = this.data.buddyInstance;
    }
    if (this.data.members) {
      this.members = this.data.members;
    }
    this.buddyActivityForm = new UntypedFormGroup({
      startTime: new UntypedFormControl(''),
      endTime: new UntypedFormControl(''),
      userId: new UntypedFormControl(-1),
      includeDeletedBuddies: new UntypedFormControl(false),
    });

    this.getCSVBuddyActivitiesByBuddyIdResponse$ = this.buddyStore.getCSVBuddyActivitiesByBuddyIdResponse$;
    this.getCSVBuddyActivitiesByStudyIdResponse$ = this.buddyStore.getCSVBuddyActivitiesByStudyIdResponse$;
    this.members$ = this.membersSubject.asObservable();
  }

  public get f() {
    return this.buddyActivityForm;
  }

  ngOnInit(): void {
    if (this.studyId) {
      this.membersSubject.next(this.members);
    }
    if (this.buddyInstance) {
      this.membersSubject.next(
        this.members.filter((user) =>
          this.buddyInstance.attributes.buddies_ids.map(id => parseInt(id.toString(), 10)).includes(user.id),
        )
      );
    }
  }

  public resetAllFilter(): void {
    this.f.get('startTime').patchValue('');
    this.f.get('endTime').patchValue('');
    this.f.get('userId').patchValue(-1);
  }

  public resetFilter(): void {
    this.filter.userSelection = '';
  }

  public onKeyFilter(): void {
    const filterResults = this.members.filter(
      (user) =>
        user.attributes.name?.toLowerCase().includes(this.filter['userSelection'].toLowerCase()) ||
        user.attributes.email.toLowerCase().includes(this.filter['userSelection'].toLowerCase()),
    );
    this.membersSubject.next(filterResults);
  }

  public generateCSVBuddyActivitiesReportByBuddyId(): void {
    if (this.getCSVBuddyActivitiesByBuddyIdResponse.value !== 'DEFAULT') {
      return;
    }
    this.getCSVBuddyActivitiesByBuddyIdResponse.next('LOADING');

    const startTime =
      this.f.get('startTime').value && this.f.get('startTime').value !== ''
        ? this.helperService.convertStringToUnixTimestamp(this.f.get('startTime').value)
        : undefined;
    const endTime =
      this.f.get('endTime').value && this.f.get('endTime').value !== ''
        ? this.helperService.convertStringToUnixTimestamp(this.f.get('endTime').value)
        : undefined;
    const userId = this.f.get('userId').value && this.f.get('userId').value !== -1 ? this.f.get('userId').value : undefined;
    this.buddyStore.exportCSVBuddyActivitiesByBuddyId({
      buddyId: this.buddyInstance.id,
      userId,
      startTime,
      endTime,
    });
    this.subscriptions.push(
      this.getCSVBuddyActivitiesByBuddyIdResponse$.subscribe((value) => {
        if (value instanceof HttpErrorResponse) {
          this.getCSVBuddyActivitiesByBuddyIdResponse.next('FAILURE');
          setTimeout(() => {
            this.getCSVBuddyActivitiesByBuddyIdResponse.next('DEFAULT');
          }, 2500);
        } else {
          if (value.state && value.state === 'DONE') {
            this.getCSVBuddyActivitiesByBuddyIdResponse.next('SUCCESS');
            setTimeout(() => {
              this.getCSVBuddyActivitiesByBuddyIdResponse.next('DEFAULT');
            }, 2500);
          }
        }
      })
    );
  }

  public generateCSVBuddyActivitiesReportByStudyId(): void {
    if (this.getCSVBuddyActivitiesByStudyIdResponse.value !== 'DEFAULT') {
      return;
    }
    this.getCSVBuddyActivitiesByStudyIdResponse.next('LOADING');

    const startTime =
      this.f.get('startTime').value && this.f.get('startTime').value !== ''
        ? this.helperService.convertStringToUnixTimestamp(this.f.get('startTime').value)
        : undefined;
    const endTime =
      this.f.get('endTime').value && this.f.get('endTime').value !== ''
        ? this.helperService.convertStringToUnixTimestamp(this.f.get('endTime').value)
        : undefined;
    const userId = this.f.get('userId').value && this.f.get('userId').value !== -1 ? this.f.get('userId').value : undefined;
    const includeDeletedBuddies = this.f.get('includeDeletedBuddies').value || undefined;
    this.buddyStore.exportCSVBuddyActivitiesByStudyId({
      studyId: this.studyId,
      userId,
      startTime,
      endTime,
      includeDeletedBuddies,
    });
    this.subscriptions.push(
      this.getCSVBuddyActivitiesByStudyIdResponse$.subscribe((value) => {
        if (value instanceof HttpErrorResponse) {
          this.getCSVBuddyActivitiesByStudyIdResponse.next('FAILURE');
          setTimeout(() => {
            this.getCSVBuddyActivitiesByStudyIdResponse.next('DEFAULT');
          }, 2500);
        } else {
          if (value.state && value.state === 'DONE') {
            this.getCSVBuddyActivitiesByStudyIdResponse.next('SUCCESS');
            setTimeout(() => {
              this.getCSVBuddyActivitiesByStudyIdResponse.next('DEFAULT');
            }, 2500);
          }
        }
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
